import React, { Component } from 'react';
import { BackHandler, Keyboard } from 'react-native';

import { Tabs } from '@vfgroup-oneplatform/foundation/Components';
import { withTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { ToBiEmojis } from '@vfgroup-oneplatform/foundation/ToBi';
import Navigation from '@vfgroup-oneplatform/foundation/Utils/Navigation';
import I18n from 'i18n-js';
import { isString as _isString, trimEnd as _trimEnd, isFunction as _isFunction } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import BlockBox from '../../Components/BlockBox';
import ChangeLanguage from '../../Components/ChangeLanguage';
import Status from '../../Utils/AuthStatusEnum';
import LoginTestKeys from '../../Utils/LoginTestKeys';
import { LoginTrackingManager } from '../../Utils/TrackingManagers';
import AuthContainer from '../AuthContainer/AuthContainer';
import withValidation from '../Hoc/withValidation';

import styles from './LoginByEmail.Styles';
import LoginForm from './LoginForm';

const TabBarData = [
  {
    label: 'upfront_login_tab_bar_first',
    inputsLabels: ['upfront_email_or_phone_number_hint', 'upfront_password_hint'],
    validationMessage: 'upfront_login_error_enter_valid_email',
  },
  {
    label: 'upfront_login_tab_bar_second',
    inputsLabels: ['upfront_login_payer_number', 'upfront_password_hint'],
    validationMessage: 'upfront_login_error_enter_valid_number',
  },
];
const UPFRONT_LOGIN_MODES = {
  ACCESS_SECURE_CONTENT: 'ACCESS_SECURE_CONTENT',
};

const getEmailValidationState = (state, isPasswordCorrectLength) => {
  const newShouldWink = !(_isString(state.password) && isPasswordCorrectLength);

  return {
    loginDisabled: !(_isString(state.password) && isPasswordCorrectLength) || state.showErrorBox,
    isValid: true,
    tobiEmoji:
      newShouldWink && state.tobiEmoji === ToBiEmojis.Wink
        ? undefined
        : _isString(state.password) && isPasswordCorrectLength && state.tobiShouldWink
          ? ToBiEmojis.Wink
          : state.tobiEmoji,
    tobiShouldWink: newShouldWink,
  };
};

class LoginByEmail extends Component {
  static getDerivedStateFromProps(props, state) {
    const isPasswordCorrectLength = state.password.length >= (props.passwordLength ?? 1);

    if (
      props.isValidPhone === null &&
      props.isValidEmail === null &&
      (!state.loginDisabled || !state.isValid || state.showErrorBox)
    ) {
      return {
        loginDisabled: true,
        isValid: true,
        showErrorBox: false,
        tobiEmoji: undefined,
      };
    } else if (
      (props.isValidPhone === 1 || props.isValidEmail === 1) &&
      (!state.isValid ||
        state.loginDisabled === (_isString(state.password) && isPasswordCorrectLength))
    ) {
      return getEmailValidationState(state, isPasswordCorrectLength);
    } else if (
      props.isValidPhone === 0 &&
      props.isValidEmail === 0 &&
      (state.isValid || state.showErrorBox)
    ) {
      return {
        isValid: false,
        showErrorBox: false,
        tobiEmoji: undefined,
        tobiShouldWink: true,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);

    const showRememberMe = Navigation.instance.getParam(this, 'showRememberMe', true);
    const withFirstLabel = Navigation.instance.getParam(this, 'withFirstLabel', true);
    const withSecondLabel = Navigation.instance.getParam(this, 'withSecondLabel', true);
    const rememberMe = Navigation.instance.getParam(this, 'rememberMe', true);
    const getRefFunction = Navigation.instance.getParam(this, 'getRef', true);

    this.state = {
      startAnimation: false,
      tobiEmoji: undefined,
      password: '',
      rememberMe,
      showRememberMe,
      withFirstLabel,
      withSecondLabel,
      loginDisabled: true,
      showErrorBox: false,
      errorMessage: '',
      subErrorMessage: '',
      isValid: true,
      showBlockDialog: false,
      forgetPasswordWidth: 145.6,
      showPasswordIcon: false,
      tobiShouldWink: true,
      isLoading: false,
      isLanguageVisible: false,
      showSecondStep: false,
      apiResponse: null,
      selectedTabIndex: 0,
    };

    this.emailOrPhone = '';
    this.emailOrPhoneRef = React.createRef();
    this.passwordRef = React.createRef();

    _isFunction(getRefFunction) && getRefFunction(this);
  }

  componentDidMount() {
    this.props.navigation.navigate('PreLogin')

    // const additionalTrackingInfo = {
    //   page_section: 'Login:Empty fields',
    //   visitor_login_status: 'logging in',
    //   ...this.getAdditionalTrackingInfo(),
    // };
    // LoginTrackingManager.trackView('page_view', additionalTrackingInfo);
    // LoginTrackingManager.trackView('visitor_login_start', additionalTrackingInfo);
  }

  componentDidUpdate(_, prevState) {
    if (prevState.loginDisabled !== this.state.loginDisabled && !this.state.loginDisabled) {
      const additionalTrackingInfo = {
        page_section: 'Login:Text fields completed',
        visitor_login_status: 'logging in',
        ...this.getAdditionalTrackingInfo(),
      };
      LoginTrackingManager.trackView('page_view', additionalTrackingInfo);
    }
  }

  onSelectedTab = (item, selectedTabIndex) => {
    this.setState(
      {
        selectedTabIndex,
      },
      () => {
        this.changeAccount();
      },
    );
  };

  onConfirmSelectLanguage = selectedLanguage => {
    const languageProps = Navigation.instance.getParam(this, 'languageProps', {});
    if (_isFunction(languageProps.onChangeLanguage)) {
      languageProps.onChangeLanguage(selectedLanguage);
      this.closeLanguageModal();
    }
  };

  onOptionsViewLayout = ({ nativeEvent: { layout } }) => {
    const { width } = layout;
    this.optionsViewWidth = width;

    this.setForgetPasswordViewWidth(this.optionsViewWidth, this.rememberMeViewWidth);
  };

  onRememberMeViewLayout = ({ nativeEvent: { layout } }) => {
    const { width } = layout;
    this.rememberMeViewWidth = width;

    this.setForgetPasswordViewWidth(this.optionsViewWidth, this.rememberMeViewWidth);
  };

  onEmailOrPhoneChange = emailOrPhone => {
    if (!emailOrPhone) {
      this.setState({ tobiShouldWink: true });
    }
    this.emailOrPhone = emailOrPhone;

    const { handleValidation } = this.props;
    const emailOrPhoneWithNoSpaces = _trimEnd(emailOrPhone || '');

    handleValidation(emailOrPhoneWithNoSpaces);
  };

  onPasswordChange = password => {
    this.setState({ password, showErrorBox: false, tobiEmoji: undefined });
  };

  onRememberMeChange = rememberMe => {
    this.setState({ rememberMe });
    const onRememberMeChange = Navigation.instance.getParam(this, 'onRememberMeChange', () => null);
    _isFunction(onRememberMeChange) && onRememberMeChange(rememberMe);
  };

  onPressRegister = () => {
    const onRegister = Navigation.instance.getParam(this, 'onRegister', () => null);
    onRegister();
  };

  onPressThirdLabel = () => {
    const onThirdLabelPress = Navigation.instance.getParam(this, 'onThirdLabelPress', () => null);
    _isFunction(onThirdLabelPress) && onThirdLabelPress();
  };

  onLoginPress = async () => {
    const additionalTrackingInfo = this.getAdditionalTrackingInfo();
    LoginTrackingManager.trackEvent('ui_interaction', {
      event_action: 'OnClick',
      event_category: 'Button',
      event_label: 'Log in',
      page_section: 'Login',
      visitor_login_status: 'logging in',
      ...additionalTrackingInfo,
    });

    Keyboard.dismiss();
    const { password, selectedTabIndex } = this.state;
    const { emailOrPhone } = this;
    const onSuccess = Navigation.instance.getParam(this, 'onSuccess', () => null);
    const onLogin = Navigation.instance.getParam(this, 'onLogin', () => null);

    const renderSecondStepModal = Navigation.instance.getParam(this, 'renderSecondStepModal', null);

    this.setState(
      {
        tobiEmoji: ToBiEmojis.Whistle,
        showErrorBox: false,
        isLoading: true,
      },
      () => {
        LoginTrackingManager.trackView('page_view', {
          ...additionalTrackingInfo,
          page_name: 'Login',
          page_section: 'Login:Loading',
          visitor_login_status: 'logging in',
          visitor_login_type: undefined,
        });
      },
    );

    try {
      const result = await onLogin(
        _trimEnd(emailOrPhone),
        password,
        this.state.rememberMe,
        selectedTabIndex,
      );
      this.setState({
        tobiEmoji: undefined,
        isLoading: false,
        apiResponse: result,
      });

      switch (result.status) {
        case Status.Success:
          LoginTrackingManager.trackView('visitor_login_complete', {
            page_section: 'Login',
            visitor_login_status: 'logged in',
            ...additionalTrackingInfo,
          });
          result.withSecondStep && renderSecondStepModal
            ? this.setState({ showSecondStep: true })
            : onSuccess(result.response, result);

          break;
        case Status.Failed:
          this.setState({
            loginDisabled: false,
            showErrorBox: true,
            errorMessage: result.errorMessage,
            subErrorMessage: result.subErrorMessage,
            isValid: true,
            tobiEmoji: ToBiEmojis.SadWithoutTransition,
          });
          break;
      }
    } catch (error) {
      LoginTrackingManager.trackView('page_view', {
        page_section: 'Login:Error',
        visitor_login_status: 'logging in',
        ...additionalTrackingInfo,
      });

      this.setState({
        tobiEmoji: undefined,
        isLoading: false,
      });
      switch (error.status) {
        case Status.Failed:
          this.setState({
            showErrorBox: true,
            loginDisabled: false,
            errorMessage: error.errorMessage,
            subErrorMessage: error.subErrorMessage,
            isValid: true,
            tobiEmoji: ToBiEmojis.SadWithoutTransition,
          });
          break;
        case Status.Blocked:
          BackHandler.addEventListener('hardwareBackPress', this.handleBackButton);
          this.setState({
            showBlockDialog: true,
            tobiEmoji: ToBiEmojis.Surprise,
          });
          break;
      }
    }
  };

  onEndAnimation = () => {
    this.setState({ startAnimation: false, tobiEmoji: undefined });
  };
  onPressLanguageLabel = () => {
    this.setState({ isLanguageVisible: true });
  };
  getAdditionalTrackingInfo = () => {
    const getAdditionalTrackingInfo = Navigation.instance.getParam(
      this,
      'getAdditionalTrackingInfo',
      () => { },
    );
    const additionalTrackingInfo =
      _isFunction(getAdditionalTrackingInfo) && getAdditionalTrackingInfo();
    return {
      page_name: 'Upfront Login',
      visitor_login_type: 'username - password',
      ...additionalTrackingInfo,
    };
  };

  getErrorProps = (showErrorBox, errorMessage) => {
    const errorConfig = Navigation.instance.getParam(this, 'errorConfig', {});
    return {
      showErrorBox,
      errorMessage,
      ...errorConfig,
    };
  };
  getErrorPropsMemoized = memoize(this.getErrorProps);

  getForgetPasswordStyle = forgetPasswordWidth => {
    return {
      width: forgetPasswordWidth,
      alignItems: forgetPasswordWidth >= 145.6 ? 'flex-end' : 'stretch',
    };
  };

  getForgetPasswordStyleMemoized = memoize(this.getForgetPasswordStyle);
  getProperModeProps = (
    mode,
    withChangeLanguage,
    withFirstLabel,
    withSecondLabel,
    withThirdLabel,
  ) => {
    const isAccessSecureContentMode = mode == UPFRONT_LOGIN_MODES.ACCESS_SECURE_CONTENT;
    return {
      title: isAccessSecureContentMode
        ? 'upfront_login_access_secure_content_title'
        : 'login_title_text',
      subTitle: isAccessSecureContentMode ? 'upfront_login_access_secure_content_subtitle' : '',
      withCloseButton: isAccessSecureContentMode,
      withChangeLanguage: isAccessSecureContentMode ? false : withChangeLanguage,
      withFirstLabel: isAccessSecureContentMode ? false : withFirstLabel,
      withSecondLabel: isAccessSecureContentMode ? false : withSecondLabel,
      withThirdLabel: isAccessSecureContentMode ? false : withThirdLabel,
      onClosePress: () => Navigation.instance.goBack(null),
    };
  };

  setForgetPasswordViewWidth = (optionsViewWidth, rememberMeViewWidth) => {
    if (optionsViewWidth && rememberMeViewWidth) {
      const remainingSpace = optionsViewWidth - (rememberMeViewWidth + 16);
      const width = remainingSpace >= 145.6 ? 145.6 : remainingSpace;
      this.setState({ forgetPasswordWidth: width });
    }
  };

  closeLanguageModal = () => {
    this.setState({
      isLanguageVisible: false,
    });
  };

  handleBackButton = () => {
    this.closeBlockDialog();
    return true;
  };

  forgottenPassword = () => {
    const onForgetPassword = Navigation.instance.getParam(this, 'onForgetPassword', () => null);
    onForgetPassword();
  };

  resetAccount = () => {
    BackHandler.removeEventListener('hardwareBackPress', this.handleBackButton);
    const onResetAccount = Navigation.instance.getParam(this, 'onResetAccount', () => null);
    onResetAccount(this.emailOrPhone);
    this.changeAccount();
  };

  changeAccount = () => {
    const iseEmailOrPhoneField =
      this.emailOrPhoneRef &&
      this.emailOrPhoneRef.current &&
      _isFunction(this.emailOrPhoneRef.current.setValue);

    const isePasswordField =
      this.passwordRef &&
      this.passwordRef.current &&
      _isFunction(this.passwordRef.current.setValue);

    iseEmailOrPhoneField && this.emailOrPhoneRef.current.setValue('');
    isePasswordField && this.passwordRef.current.setValue('');

    this.emailOrPhone = '';

    this.setState({
      password: '',
      loginDisabled: true,
      showBlockDialog: false,
      tobiEmoji: undefined,
    });
    Keyboard.dismiss();
    BackHandler.removeEventListener('hardwareBackPress', this.handleBackButton);
  };

  closeBlockDialog = () => {
    this.setState({
      showBlockDialog: false,
    });
  };

  closeSecondStep = () => this.setState({ showSecondStep: false });

  render() {
    const {
      password,
      rememberMe,
      loginDisabled,
      forgetPasswordWidth,
      startAnimation,
      errorMessage,
      tobiEmoji,
      showBlockDialog,
      isLoading,
      isValid,
      showErrorBox,
      subErrorMessage,
      showRememberMe,
      withFirstLabel,
      withSecondLabel,
      isLanguageVisible,
      showSecondStep,
      apiResponse,
      selectedTabIndex,
    } = this.state;
    const { emailOrPhone } = this;
    const { theme } = this.props;
    const showErrorBoxNotvalid = !isValid || showErrorBox;
    const showSecondaryButton = Navigation.instance.getParam(this, 'showSecondaryButton', false);

    const withThirdLabel = Navigation.instance.getParam(this, 'withThirdLabel', false);

    const secondaryButtonProps = Navigation.instance.getParam(this, 'secondaryButtonProps', {});
    const renderFooter = Navigation.instance.getParam(this, 'renderFooter', () => { });

    const withBiometricsLogin = Navigation.instance.getParam(this, 'withBiometricsLogin', false);

    const onBiometricsSuccess = Navigation.instance.getParam(this, 'onBiometricsSuccess', () => { });
    const onBiometricsError = Navigation.instance.getParam(this, 'onBiometricsError', () => { });
    const biometricsConfig = Navigation.instance.getParam(this, 'biometricsConfig', {});

    const renderTitle = !isLoading
      ? Navigation.instance.getParam(this, 'renderTitle', null)
      : () => { };

    const insideBackBtn = Navigation.instance.getParam(this, 'insideBackBtn', false);
    const backBtnPress = Navigation.instance.getParam(this, 'backBtnPress', () => { });

    const withChangeLanguage = Navigation.instance.getParam(this, 'withChangeLanguage', false);
    const languageProps = Navigation.instance.getParam(this, 'languageProps', {});
    const mode = Navigation.instance.getParam(this, 'mode', '');

    const titleStyle = Navigation.instance.getParam(this, 'titleStyle', {});

    const forgotPasswordContainerStyle = Navigation.instance.getParam(
      this,
      'forgotPasswordContainerStyle',
      {},
    );

    const forgotPasswordTextStyle = Navigation.instance.getParam(
      this,
      'forgotPasswordTextStyle',
      {},
    );

    const keyboardType = Navigation.instance.getParam(this, 'keyboardType', 'default');

    const renderSecondStepModal = Navigation.instance.getParam(this, 'renderSecondStepModal', null);

    const showSecondTabLogin = Navigation.instance.getParam(this, 'showSecondTabLogin', false);

    return (<></>)

    // return (
    //   <React.Fragment>
    //     {showSecondStep ? renderSecondStepModal?.(apiResponse, this.closeSecondStep) : null}
    //     <AuthContainer
    //       firstLabelTitle="upfront_not_registered_with_my_vodafone_label"
    //       secondLabelTitle="upfront_register_now_label"
    //       thirdLabelTitle="login_more_information_label"
    //       thirdLabelTitleText="login_more_information_text"
    //       firstLabelDisabled
    //       secondLabelOnPress={this.onPressRegister}
    //       thirdLabelOnPress={this.onPressThirdLabel}
    //       firstLabelTestID={LoginTestKeys.loginNotRegisteredTxt}
    //       secondLabelTestId={LoginTestKeys.loginRegisterNowLink}
    //       bubbleText="upfront_login_helper_information"
    //       tobiSize={114}
    //       tobiEmoji={tobiEmoji}
    //       renderTobiLessIconView={Navigation.instance.getParam(this, 'renderLoginIconView', null)}
    //       renderFooter={renderFooter}
    //       errorProps={this.getErrorPropsMemoized(showErrorBox, errorMessage)}
    //       titleStyle={titleStyle}
    //       renderTitle={renderTitle}
    //       onEndAnimation={this.onEndAnimation}
    //       childrenContainerAccessibilityLabel={LoginTestKeys.childrenContainer}
    //       startAnimation={startAnimation}
    //       tobiTestId={LoginTestKeys.authContainerTobiIcon}
    //       withBiometricsLogin={withBiometricsLogin}
    //       biometricsConfig={biometricsConfig}
    //       onBiometricsSuccess={onBiometricsSuccess}
    //       onBiometricsError={onBiometricsError}
    //       isLoading={isLoading}
    //       loadingText="verification_loading_login_text"
    //       insideBackBtn={insideBackBtn && !isLoading}
    //       backBtnPress={backBtnPress}
    //       languageProps={languageProps}
    //       withChangeLanguage={withChangeLanguage}
    //       onPressLanguageLabel={this.onPressLanguageLabel}
    //       {...this.getProperModeProps(
    //         mode,
    //         withChangeLanguage,
    //         withFirstLabel,
    //         withSecondLabel,
    //         withThirdLabel,
    //       )}
    //     >
    //       {showSecondTabLogin && (
    //         <Tabs
    //           data={TabBarData}
    //           onSelectedTab={this.onSelectedTab}
    //           tabsStyle={styles.tabsContainer}
    //           scrollable={false}
    //           initialActiveTab={this.state.selectedTabIndex}
    //         />
    //       )}
    //       <LoginForm
    //         password={password}
    //         rememberMe={rememberMe}
    //         loginDisabled={loginDisabled}
    //         forgetPasswordWidth={forgetPasswordWidth}
    //         isValid={isValid}
    //         showErrorBox={showErrorBox}
    //         subErrorMessage={subErrorMessage}
    //         showRememberMe={showRememberMe}
    //         emailOrPhone={emailOrPhone}
    //         showErrorBoxNotvalid={showErrorBoxNotvalid}
    //         showSecondaryButton={showSecondaryButton}
    //         secondaryButtonProps={secondaryButtonProps}
    //         emailOrPhoneRef={this.emailOrPhoneRef}
    //         onEmailOrPhoneChange={this.onEmailOrPhoneChange}
    //         validMessage={TabBarData[selectedTabIndex].validationMessage}
    //         passwordRef={this.passwordRef}
    //         onPasswordChange={this.onPasswordChange}
    //         onOptionsViewLayout={this.onOptionsViewLayout}
    //         onRememberMeViewLayout={this.onRememberMeViewLayout}
    //         onRememberMeChange={this.onRememberMeChange}
    //         getForgetPasswordStyleMemoized={this.getForgetPasswordStyleMemoized}
    //         forgotPasswordContainerStyle={forgotPasswordContainerStyle}
    //         forgotPasswordTextStyle={forgotPasswordTextStyle}
    //         forgottenPassword={selectedTabIndex === 0 && this.forgottenPassword}
    //         onLoginPress={this.onLoginPress}
    //         keyboardType={keyboardType}
    //         userNameInputLabel={TabBarData[selectedTabIndex].inputsLabels[0]}
    //         passwordInputLabel={TabBarData[selectedTabIndex].inputsLabels[1]}
    //       />
    //     </AuthContainer>
    //     {showBlockDialog && (
    //       <BlockBox
    //         onPrimaryBtnPress={this.resetAccount}
    //         primaryBtnTitle="upfront_login_reset_account_login_button"
    //         onSecondaryBtnPress={this.changeAccount}
    //         secondaryBtnTitle="upfront_login_different_account_login_button"
    //         text="upfront_login_blocked_warning_text"
    //         textPlaceholder={emailOrPhone}
    //         onClose={this.closeBlockDialog}
    //         theme={theme}
    //         title="upfront_login_blocked_warning_title"
    //         subtitle="upfront_login_blocked_warning_subtitle"
    //       />
    //     )}
    //     {withChangeLanguage && (
    //       <ChangeLanguage
    //         theme={theme}
    //         isVisible={isLanguageVisible}
    //         closeModal={this.closeLanguageModal}
    //         languages={languageProps?.languages}
    //         currentSelectedLanguage={I18n.currentLocale()}
    //         onConfirmSelectLanguage={this.onConfirmSelectLanguage}
    //       />
    //     )}
    //   </React.Fragment>
    // );
  }
}

LoginByEmail.defaultProps = {
  theme: LightThemeColors,
};
LoginByEmail.propTypes = {
  theme: PropTypes.object,
  navigation: PropTypes.object.isRequired,
  passwordLength: PropTypes.number,
  isValidPhone: PropTypes.number,
  isValidEmail: PropTypes.number,
  handleValidation: PropTypes.func,
};
export { UPFRONT_LOGIN_MODES, getEmailValidationState };
export default withValidation(withTheme(LoginByEmail));
